import { createFileRoute, notFound } from "@tanstack/react-router"

import { PageNotFound } from "@/components/feedback/page-not-found"
import { UnavailableAlert } from "@/components/feedback/unavailable-alert"
import { Container } from "@/components/layout/container"
import { SiteShell } from "@/components/layout/site-shell"
import { NewsArticle } from "@/components/news/news-article"
import { JsonLd, absoluteUrl, organizationRef } from "@/components/seo/json-ld"
import {
  imageMeta,
  layoutMeta,
  templateTitle,
  absoluteUrl as metaUrl,
  type HeadMeta,
} from "@/lib/metadata"
import { notFoundHead } from "@/lib/not-found"
import { siteConfig } from "@/lib/site"
import { getNewsPost } from "@/server/web"

/**
 * One news post. Read by the service and kept 300 s (the Next page's
 * `revalidate`; admin edits revalidate it at once). A database that can't be
 * read is not a missing post: the page says it's unavailable instead of 404.
 */
export const Route = createFileRoute("/news/$slug")({
  loader: async ({ params }) => {
    const data = await getNewsPost({ data: params.slug })
    if (data.read && !data.read.post) throw notFound()
    return data
  },
  head: ({ loaderData }) => {
    if (!loaderData) return notFoundHead({ layout: true })
    const post = loaderData.read?.post
    if (!post) {
      // `generateMetadata` for an unreadable database.
      return {
        meta: [
          ...layoutMeta(),
          { title: templateTitle("News") },
          { name: "robots", content: "noindex" },
        ],
      }
    }
    const path = `/news/${post.slug}`
    // Just the title: previews show "Anysola" as the site name above it.
    const title = post.title
    // A post's own image is what X, WhatsApp and Discord show; the card from
    // `opengraph-image` is the fallback.
    const image = post.image
      ? { url: absoluteUrl(post.image.url), alt: post.image.alt }
      : {
          url: absoluteUrl(`${path}/opengraph-image`),
          width: 1200,
          height: 630,
          alt: post.title,
        }
    const meta: HeadMeta[] = [
      ...layoutMeta(),
      { title: templateTitle(title) },
      { name: "description", content: post.summary },
      { property: "og:title", content: title },
      { property: "og:description", content: post.summary },
      { property: "og:url", content: metaUrl(path) },
      { property: "og:site_name", content: siteConfig.name },
      { property: "og:locale", content: "en_US" },
      ...imageMeta("og", image),
      ...(post.video
        ? [
            { property: "og:video", content: absoluteUrl(post.video) },
            { property: "og:video:type", content: "video/mp4" },
          ]
        : []),
      { property: "og:type", content: "article" },
      {
        property: "article:published_time",
        content: `${post.date}T00:00:00.000Z`,
      },
      { property: "article:section", content: post.category },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: post.summary },
      ...imageMeta("twitter", image),
    ]
    return {
      meta,
      links: [
        { rel: "canonical", href: metaUrl(path) },
        {
          rel: "alternate",
          type: "application/rss+xml",
          href: metaUrl("/news/rss.xml"),
        },
      ],
    }
  },
  component: NewsPostPage,
  notFoundComponent: PageNotFound,
})

function NewsPostPage() {
  const { read, more } = Route.useLoaderData()
  if (!read) {
    return (
      <SiteShell>
        <h1 className="sr-only">News</h1>
        <Container className="pt-10 pb-14 sm:pt-12 sm:pb-16">
          <UnavailableAlert title="This post is unavailable right now." />
        </Container>
      </SiteShell>
    )
  }
  const post = read.post!

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: post.title,
    description: post.summary,
    datePublished: `${post.date}T00:00:00.000Z`,
    mainEntityOfPage: absoluteUrl(`/news/${post.slug}`),
    image: [
      ...(post.image ? [absoluteUrl(post.image.url)] : []),
      absoluteUrl(`/news/${post.slug}/opengraph-image`),
    ],
    articleSection: post.category,
    author: organizationRef(),
    publisher: organizationRef(),
    ...(post.video
      ? {
          video: {
            "@type": "VideoObject",
            name: post.title,
            description: post.summary,
            contentUrl: absoluteUrl(post.video),
            thumbnailUrl: post.image ? absoluteUrl(post.image.url) : undefined,
            uploadDate: `${post.date}T00:00:00.000Z`,
          },
        }
      : {}),
  }

  return (
    <SiteShell>
      <JsonLd data={jsonLd} />
      <NewsArticle post={post} more={more} />
    </SiteShell>
  )
}
